import type { ConnectionQuality, PeerId, Quality, ScreenSurface } from '@live-stream/shared'
import {
  cameraCodecFor,
  chooseTransport,
  MeshTransport,
  SfuTransport,
  type ConnectionState,
  type ContentHint,
  type MediaTransport,
  type MeshSignaling,
  type PeerStats,
  type PublishedTrack,
  type ScreenProfile,
  type SendProfile,
  type TrackKind,
  type TransportStats,
} from '@live-stream/media'
import type { HttpClient } from '../http.js'
import type { Room } from '../room.js'
import type { AppSocket } from '../socket.js'
import type { KeyValueStore } from '../storage.js'
import { derived, patch, readonly, writable, type ReadableStore } from '../store.js'
import { higherQuality } from '../stage/layout.js'
import { AudioLevelMeter, type AudioLevelState } from './audio-level.js'
import {
  DeviceList,
  labelOf,
  loadDevicePrefs,
  preferredDevice,
  saveDevicePref,
  type DevicePrefs,
} from './devices.js'
import { LocalMedia } from './local-media.js'
import { QUALITY_RANK, QualityTracker, ownQuality, rateLink, windowOf } from './quality.js'
import { captureScreen, ScreenCaptureError } from './screen-capture.js'
import { captureFailureMessage, defaultHint } from './screen-share.js'

export interface RemoteStream {
  peerId: PeerId
  stream: MediaStream
  hasVideo: boolean
  hasAudio: boolean
}

/** One link's last window, for the diagnostics: which threshold is tripping the rating. */
export interface LinkNumbers {
  rttMs: number | null
  jitterMs: number
  /** Percent of packets lost in the window. */
  lossPct: number
  freezes: number
}

/** Our own presentation, while it lasts. */
export interface LocalShare {
  stream: MediaStream
  video: PublishedTrack
  audio: PublishedTrack | null
  surface: ScreenSurface
  hint: ContentHint
  trackId: string
}

/** Why our share ended — decides what, if anything, is said and sent. */
export type ShareEnd = 'user' | 'browser' | 'refused' | 'replaced' | 'host' | 'leave'

export interface MediaOptions {
  /**
   * Force a transport: `'mesh'` for the A/B and the rollback. Anything else
   * (or nothing) means the SFU when the server offers one. A function is
   * asked each time a call starts.
   */
  transport?: string | null | (() => string | null)
  /** Relay every packet through TURN: for testing a restrictive network. */
  relayOnly?: boolean
}

export interface MediaState {
  /** Which transport carries this call: 'sfu' when the server offers one, unless forced to mesh. */
  transportName: string | null
  remotes: Record<PeerId, RemoteStream>
  /** Other people's presentations, one stream per presenter, apart from their camera. */
  screens: Record<PeerId, RemoteStream>
  share: LocalShare | null
  /** The picker is open, or the tracks are being published. */
  shareBusy: boolean
  /** How our share is encoded toward each peer — the room's cap, the hint's trade-off. */
  screenProfile: ScreenProfile | null
  /** What is actually going out for the share toward each peer. */
  screenSentTo: Record<PeerId, string>
  /** How the packets to each peer travel: direct, or a relay over UDP, TCP or TLS. */
  linkPath: Record<PeerId, string>
  peerStates: Record<PeerId, ConnectionState>
  starting: boolean
  started: boolean
  /** True while the green room is opening the camera and microphone. */
  previewing: boolean
  /**
   * Own connection quality: the best of our links, rated every two seconds
   * with hysteresis (see quality.ts). Null until there is a link to rate.
   */
  quality: ConnectionQuality | null
  /** Per-link verdicts, for the tile tooltip: how the link from them to us looks. */
  linkQuality: Record<PeerId, ConnectionQuality>
  /** The numbers behind each link's rating, from the last two-second window. */
  linkNumbers: Record<PeerId, LinkNumbers>
  /** What the camera encoder is asked to send toward each peer. */
  sendProfile: SendProfile | null
  /** What is actually going out toward each peer (outbound-rtp): the proof that a downscale took. */
  sentTo: Record<PeerId, string>
  /** What arrives from each peer: the picture's size and codec, for diagnosis. */
  receivedFrom: Record<PeerId, string>
  /** Each layer of our camera being encoded, with the encoder's target and limit; empty without a camera. */
  sendLayers: string
  /** Non-fatal problems worth telling the user about (degraded, not broken). */
  warning: string | null
  /** Something stopped media from starting at all. */
  startError: string | null
  /**
   * The browser refused to play remote audio until this document sees a user
   * gesture. Rare on desktop (the join click counts), routine on iOS Safari.
   */
  audioBlocked: boolean
  relayOnly: boolean
  /** Remembered device choices. Validated against the real list after the first start. */
  prefs: DevicePrefs
  /** Output device for every remote audio element. '' is the system default. */
  speakerId: string
  /** A device switch is in flight; the picker disables meanwhile. */
  switching: boolean
  /** The published microphone track's enabled flag, mirrored (the DOM property is not observable). */
  micMuted: boolean
  /**
   * Save data: nobody's camera is received — sound and shared screens
   * only. For someone on mobile data or a weak connection. Remembered on
   * this device.
   */
  dataSaver: boolean
  /**
   * This page has been out of sight for a while (another tab, another app):
   * cameras are not received until it is back. Sound carries on.
   */
  backgrounded: boolean
}

interface TurnResponse {
  iceServers: RTCIceServer[]
  expiresAt: number
}

const QUALITY_POLL_MS = 2_000
/**
 * Save data used to be remembered on the device, so a tap on it in one call hid everyone's camera
 * in every call after, with nothing on the screen to say so. It is now for this page only; the
 * old saved setting is cleared the first time it is met.
 */
const OLD_DATA_SAVER_KEY = 'ls_data_saver'
/**
 * How long a page must be out of sight before cameras stop arriving: a
 * glance at another tab should not cost a picture that takes a moment to
 * come back.
 */
const BACKGROUND_AFTER_MS = 5_000

/** Forget a setting an earlier version kept; storage that refuses (a private window) has none. */
function dropFlag(store: KeyValueStore | null, key: string): void {
  try {
    store?.removeItem(key)
  } catch {
    // Nothing kept, or nothing to remove it with.
  }
}

/** VP9 for the camera where it is safe (sfu-policy.ts cameraCodecFor), from this browser. */
function cameraCodecHere(): 'vp9' | 'vp8' {
  if (typeof navigator === 'undefined') return 'vp8'
  const caps =
    typeof RTCRtpSender !== 'undefined' && RTCRtpSender.getCapabilities
      ? RTCRtpSender.getCapabilities('video')
      : null
  const mobile =
    (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile ??
    /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent)
  return cameraCodecFor({
    userAgent: navigator.userAgent,
    mobile,
    encodesVp9: Boolean(caps?.codecs.some((c) => c.mimeType.toLowerCase() === 'video/vp9')),
  })
}

/**
 * Owns the media plane on the client.
 *
 * Nothing above this touches WebRTC — it talks to a MediaTransport, and an
 * ESLint rule bans RTCPeerConnection outside packages/media. That boundary
 * is what made the SFU migration a transport swap instead of a rewrite
 * (plan §1 D1).
 */
export class Media {
  /** The camera and microphone themselves. */
  readonly local = new LocalMedia()
  /** The devices there are to choose from. */
  readonly devices = new DeviceList()
  private readonly store: ReturnType<typeof writable<MediaState>>
  readonly state: ReadableStore<MediaState>
  private readonly transportStore = writable<MediaTransport | null>(null)
  /** The transport carrying the call, once prepared. */
  readonly transport: ReadableStore<MediaTransport | null> = readonly(this.transportStore)

  /**
   * The analyser listens to a CLONE of the microphone track, kept enabled
   * while the published track is muted.
   *
   * Muting sets `enabled = false` on the published track, and a disabled
   * track feeds the analyser silence too — so nobody could be told "you're
   * talking, but you're muted", which is the one thing a muted person needs
   * to hear. The clone shares the source and has its own enabled flag. It is
   * stopped whenever the track it shadows goes away, so the mic indicator
   * never outlives the call.
   */
  private readonly analysisTrack = writable<MediaStreamTrack | null>(null)
  private readonly meter: AudioLevelMeter
  /** The microphone level and our own detector's verdict, before mute is considered. */
  readonly audioLevel: ReadableStore<AudioLevelState>

  /** Everyone's camera streams, as a list. */
  readonly remoteList: ReadableStore<RemoteStream[]>
  /** Everyone's presentations, as a list. */
  readonly screenList: ReadableStore<RemoteStream[]>
  /**
   * Presenters whose share carries sound. Their voice is turned down a
   * little while it plays — the way Meet ducks the presenter under a
   * video's soundtrack — so the two do not fight.
   */
  readonly duckedPeers: ReadableStore<Set<PeerId>>
  /**
   * Every peer link is down at once. One link dropping is the other side;
   * all of them together is our own network — and worth acting on before
   * the socket's heartbeat notices anything.
   */
  readonly allLinksDown: ReadableStore<boolean>
  /** Speech on the mic AND we are sending it: the ring, and what gets reported. */
  readonly speaking: ReadableStore<boolean>
  /** Speech on the mic while muted: the "you're muted" hint. Local only, never sent. */
  readonly talkingWhileMuted: ReadableStore<boolean>
  /**
   * What is ACTUALLY in use, read back from the live tracks — never what was
   * asked for. A picker bound to these cannot show a device the browser
   * declined to give us.
   */
  readonly activeMicId: ReadableStore<string>
  readonly activeCameraId: ReadableStore<string>

  private published: { camera?: PublishedTrack; mic?: PublishedTrack } = {}
  /** We may send our microphone (a big meeting's attendees may not, until let speak). */
  private micAllowed = true
  /** We may send our camera (a big meeting's attendees may not, until let speak). */
  private cameraAllowed = true
  /** The microphone opening again after being allowed; unmuting waits for it. */
  private micOpening: Promise<void> = Promise.resolve()
  private readonly linkTrackers = new Map<PeerId, QualityTracker>()
  private readonly lastSamples = new Map<PeerId, PeerStats>()
  private qualityTimer: ReturnType<typeof setInterval> | null = null
  private unfollowDevices: (() => void) | null = null
  private stageWants: Record<PeerId, Quality> = {}
  private recorderWants: Record<PeerId, Quality> | null = null

  constructor(
    private readonly http: HttpClient,
    private readonly room: Room,
    private readonly localStorage: KeyValueStore | null,
    private readonly options: MediaOptions = {},
  ) {
    this.store = writable<MediaState>({
      transportName: null,
      remotes: {},
      screens: {},
      share: null,
      shareBusy: false,
      screenProfile: null,
      screenSentTo: {},
      linkPath: {},
      peerStates: {},
      starting: false,
      started: false,
      previewing: false,
      quality: null,
      linkQuality: {},
      linkNumbers: {},
      sendProfile: null,
      sentTo: {},
      receivedFrom: {},
      sendLayers: '',
      warning: null,
      startError: null,
      audioBlocked: false,
      relayOnly: options.relayOnly === true,
      prefs: loadDevicePrefs(localStorage),
      speakerId: '',
      switching: false,
      micMuted: false,
      dataSaver: false,
      backgrounded: false,
    })
    this.state = readonly(this.store)
    dropFlag(localStorage, OLD_DATA_SAVER_KEY)
    this.watchVisibility()

    // The analysis clone follows the microphone track.
    let shadowed: MediaStreamTrack | null = null
    this.local.state.subscribe(({ audioTrack }) => {
      if (audioTrack === shadowed) return
      shadowed = audioTrack
      this.analysisTrack.get()?.stop()
      const clone = audioTrack ? audioTrack.clone() : null
      if (clone) clone.enabled = true
      this.analysisTrack.set(clone)
    })
    this.meter = new AudioLevelMeter(this.analysisTrack)
    this.audioLevel = this.meter.state

    // Two steps, so a list is a new array only when its record changed.
    const remotes = derived([this.store], (s) => s.remotes)
    const screens = derived([this.store], (s) => s.screens)
    this.remoteList = derived([remotes], (r) => Object.values(r))
    this.screenList = derived([screens], (r) => Object.values(r))
    this.duckedPeers = derived([this.screenList], (list) => {
      return new Set(list.filter((s) => s.hasAudio).map((s) => s.peerId))
    })
    this.allLinksDown = derived([this.store], (s) => {
      const states = Object.values(s.peerStates)
      return states.length > 0 && states.every((x) => x === 'reconnecting' || x === 'failed')
    })
    this.speaking = derived([this.audioLevel, this.store], (a, s) => a.speaking && !s.micMuted)
    this.talkingWhileMuted = derived(
      [this.audioLevel, this.store],
      (a, s) => a.speaking && s.micMuted,
    )
    this.activeMicId = derived(
      [this.local.state],
      (l) => l.audioTrack?.getSettings().deviceId ?? '',
    )
    this.activeCameraId = derived(
      [this.local.state],
      (l) => l.videoTrack?.getSettings().deviceId ?? l.preferredVideoDeviceId ?? '',
    )

    this.local.onTrackLost((kind) => void this.handleLost(kind))
    this.followPresenterSlot()
  }

  private get s(): MediaState {
    return this.store.get()
  }

  /** Clear the warning shown, or show one of the app's own. */
  setWarning(warning: string | null): void {
    patch(this.store, { warning })
  }

  /** The page's audio elements report whether the browser let them play. */
  setAudioBlocked(blocked: boolean): void {
    patch(this.store, { audioBlocked: blocked })
  }

  /** Adapts the Socket.IO client to the transport's signaling surface. */
  private signalingFor(sock: AppSocket): MeshSignaling {
    return {
      sendDescription: (to, sdp, tracks) =>
        sock.emit('signal:description', {
          to,
          sdp,
          ...(Object.keys(tracks).length > 0 ? { tracks } : {}),
        }),
      sendCandidate: (to, candidate) =>
        // An end-of-candidates signal carries an empty candidate string. The
        // DOM type marks the field optional; our wire schema does not.
        sock.emit('signal:candidate', {
          to,
          candidate: { ...candidate, candidate: candidate.candidate ?? '' },
        }),
      onDescription: (handler) => {
        const fn = (m: {
          from: PeerId
          sdp: RTCSessionDescriptionInit
          tracks?: Record<string, TrackKind>
        }) => handler(m.from, m.sdp, m.tracks)
        sock.on('signal:description', fn)
        return () => sock.off('signal:description', fn)
      },
      onCandidate: (handler) => {
        const fn = (m: { from: PeerId; candidate: RTCIceCandidateInit }) =>
          handler(m.from, m.candidate)
        sock.on('signal:candidate', fn)
        return () => sock.off('signal:candidate', fn)
      },
      onPeerJoined: (handler) => {
        const fn = (m: { peerId: PeerId; shouldOffer: boolean }) => handler(m.peerId, m.shouldOffer)
        sock.on('peer:joined', fn)
        return () => sock.off('peer:joined', fn)
      },
      onPeerLeft: (handler) => {
        const fn = (m: { peerId: PeerId }) => handler(m.peerId)
        sock.on('peer:left', fn)
        return () => sock.off('peer:left', fn)
      },
    }
  }

  private async fetchIceServers(): Promise<RTCIceServer[]> {
    // Credentials are ephemeral, so this is called again on every connect
    // rather than cached for the life of the tab.
    const result = await this.http.get<TurnResponse>('/turn/credentials')
    return result.iceServers
  }

  /**
   * Subscribe to signaling and fetch ICE servers. Call this BEFORE room:join
   * is emitted — it never touches the camera.
   *
   * The server announces peers, and the first offer arrives, within
   * milliseconds of the join ack. getUserMedia on a phone can take seconds
   * (permission prompt). Subscribing only after the camera was ready left a
   * window in which every signal aimed at us landed on a socket with no
   * listener — and a Socket.IO event with no listener is simply gone. The
   * host sat in have-local-offer forever; we never even knew it had offered.
   *
   * connect() attaches its listeners synchronously before its first await,
   * so callers may fire this without awaiting and still be subscribed on the
   * very next line.
   */
  async prepare(
    sock: AppSocket,
    localPeerId: PeerId,
    opts: { sfuUrl?: string | null } = {},
  ): Promise<void> {
    if (this.transportStore.get()) return

    // The SFU when the server offers one; the mesh otherwise, or when forced
    // (the A/B and the rollback). Either way the same handlers below:
    // nothing above this line can tell.
    const forced = this.options.transport
    const mode = chooseTransport(
      Boolean(opts.sfuUrl),
      (typeof forced === 'function' ? forced() : forced) ?? null,
    )
    const mesh: MediaTransport =
      mode === 'sfu' && opts.sfuUrl
        ? new SfuTransport({ localPeerId, url: opts.sfuUrl, cameraCodec: cameraCodecHere() })
        : new MeshTransport({
            localPeerId,
            signaling: this.signalingFor(sock),
            fetchIceServers: () => this.fetchIceServers(),
            ...(this.s.relayOnly ? { relayOnly: true } : {}),
          })
    this.transportStore.set(mesh)
    patch(this.store, { transportName: mesh.name })

    // The transport hands us ONE stream per peer and adds every track to it,
    // so each event here means "that stream changed", not "here is a new
    // stream". The record is rebuilt so the flags are a new snapshot; the
    // MediaStream object itself is stable, which is what lets an element
    // already bound to it pick up a track that arrives later without being
    // rebound. (Keeping only the last stream per peer is how the audio used
    // to get lost: audio arrives first, video second.)
    const describe = (peerId: PeerId, stream: MediaStream): void => {
      patch(this.store, {
        remotes: {
          ...this.s.remotes,
          [peerId]: {
            peerId,
            stream,
            hasVideo: stream.getVideoTracks().length > 0,
            hasAudio: stream.getAudioTracks().length > 0,
          },
        },
      })
    }

    // A share is its own stream, and it comes and goes within the
    // connection: when the presenter stops, their tracks end and the
    // entry is dropped, so the stage never shows a frozen last frame.
    const describeScreen = (peerId: PeerId, stream: MediaStream): void => {
      const live = (kind: 'video' | 'audio') =>
        stream.getTracks().some((t) => t.kind === kind && t.readyState === 'live')
      const hasVideo = live('video')
      const hasAudio = live('audio')
      if (!hasVideo && !hasAudio) {
        const next = { ...this.s.screens }
        delete next[peerId]
        patch(this.store, { screens: next })
        return
      }
      patch(this.store, {
        screens: { ...this.s.screens, [peerId]: { peerId, stream, hasVideo, hasAudio } },
      })
    }
    const isScreen = (kind: string) => kind === 'screen' || kind === 'screen-audio'

    mesh.on('track-added', ({ peerId, kind, stream }) => {
      if (isScreen(kind)) describeScreen(peerId, stream)
      else describe(peerId, stream)
    })

    mesh.on('track-removed', ({ peerId, kind }) => {
      if (isScreen(kind)) {
        const current = this.s.screens[peerId]
        if (current) describeScreen(peerId, current.stream)
        return
      }
      const current = this.s.remotes[peerId]
      if (current) describe(peerId, current.stream)
    })

    mesh.on('peer-left', ({ peerId }) => {
      const remotes = { ...this.s.remotes }
      delete remotes[peerId]
      const screens = { ...this.s.screens }
      delete screens[peerId]
      const peerStates = { ...this.s.peerStates }
      delete peerStates[peerId]
      patch(this.store, { remotes, screens, peerStates })
    })

    mesh.on('connection-state', ({ peerId, state }) => {
      if (peerId) patch(this.store, { peerStates: { ...this.s.peerStates, [peerId]: state } })
      // The SFU let us go while we are still in the meeting — a removal
      // meant for a seat we have since come back to, a server restart. The
      // room still lists us, so nobody would see or hear us: join it again.
      else if (state === 'closed' && this.s.started) this.rejoinSfu(mesh)
    })

    mesh.on('error', ({ code, message }) => {
      // Degraded, not dead. Say so rather than letting the call quietly
      // under-perform with no explanation.
      if (code === 'ice_servers_unavailable') patch(this.store, { warning: message })
      if (code === 'publish_failed') {
        // In plain words for the person; the detail for whoever debugs it.
        console.warn('media: publish failed', message)
        const what = message.startsWith('camera')
          ? 'Your camera'
          : message.startsWith('screen')
            ? 'Your screen'
            : 'Your microphone'
        patch(this.store, {
          warning: `${what} couldn't reach the others. Turn it off and on again to retry.`,
        })
      }
    })

    // The mesh subscribes to signaling now, before the join, and needs no
    // token. The SFU needs the token the join ack brings; start() connects.
    if (mesh instanceof MeshTransport) await mesh.connect()
    this.qualityTimer ??= setInterval(() => void this.pollQuality(), QUALITY_POLL_MS)
  }

  /**
   * Join the SFU room with the token the seat came with. Idempotent while
   * connected; after a rejoin the room holds a fresh token and a connection
   * that lapsed is made again.
   */
  async ensureSfuConnected(): Promise<boolean> {
    const sfu = this.transportStore.get()
    if (!sfu || sfu.name !== 'sfu') return true
    const { sfu: access, room } = this.room.state.get()
    if (!access) {
      patch(this.store, {
        startError:
          'The server offered no media token for this seat. Reload; if it persists, the SFU is misconfigured.',
      })
      return false
    }
    try {
      await sfu.connect(room.roomId, access.token)
      return true
    } catch (err) {
      patch(this.store, {
        startError: `Could not reach the media server: ${err instanceof Error ? err.message : String(err)}`,
      })
      return false
    }
  }

  /**
   * Join the SFU room again after it let us go: soon, then less and less
   * often, for about a minute — a network that comes back, a server that
   * restarted. What we were sending goes out again, a muted mic still muted.
   */
  private rejoinSfu(transport: MediaTransport, attempt = 0): void {
    const delays = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000]
    const delay = delays[attempt]
    if (delay === undefined) return
    setTimeout(() => {
      if (this.transportStore.get() !== transport || !this.s.started) return
      void this.ensureSfuConnected().then((ok) => {
        if (ok) {
          this.setMuted(this.s.micMuted)
          patch(this.store, { startError: null })
        } else this.rejoinSfu(transport, attempt + 1)
      })
    }, delay)
  }

  /**
   * Acquire the camera and microphone and publish them. Runs once we are
   * actually in the room — acquiring while still knocking would light
   * someone's LED in a meeting they may never be admitted to.
   *
   * Peers may already exist by now (created from signals that arrived while
   * the camera was opening). publish() attaches the tracks to each of them
   * via replaceTrack on the pre-created transceivers, which needs no
   * renegotiation — so a late camera still flows without a second round trip.
   */
  async start(): Promise<void> {
    const mesh = this.transportStore.get()
    if (!mesh) {
      patch(this.store, { startError: 'Internal: media.prepare() must run before media.start().' })
      return
    }
    if (this.s.started || this.s.starting) return
    patch(this.store, { starting: true, startError: null })

    try {
      if (!(await this.ensureSfuConnected())) return
      // The green room normally got here first, and its tracks are the ones
      // that get published — joining is a socket round trip, not a second
      // permission prompt and camera warm-up. A path with no preview still
      // acquires here.
      if (!this.local.state.get().stream) await this.acquire()

      const { audioTrack, videoTrack } = this.local.state.get()
      if (audioTrack && this.micAllowed) {
        this.published.mic = await mesh.publish(audioTrack, { kind: 'microphone' })
        // Muted in the green room stays muted: publishing switches a track on.
        this.setMuted(this.s.micMuted)
      } else if (audioTrack) {
        // Not ours to send yet: closed, not just muted.
        this.setMuted(true)
        this.local.stopAudio()
      }
      if (videoTrack && !this.cameraAllowed) {
        // Not ours to show yet: off, the light out, nothing sent.
        this.local.stopVideo()
      } else if (videoTrack) {
        this.published.camera = await mesh.publish(videoTrack, {
          kind: 'camera',
          contentHint: 'motion',
          simulcast: true,
        })
      }
      patch(this.store, { started: true })
    } catch (err) {
      // A rejection here used to escape as an unhandled promise rejection:
      // the console had a stack, and the UI showed nothing but two people
      // looking at themselves.
      patch(this.store, {
        startError:
          `Could not start the call: ${err instanceof Error ? err.message : String(err)}. ` +
          `A browser extension that patches fetch or WebRTC is a common cause — ` +
          `try a private window with extensions disabled.`,
      })
    } finally {
      patch(this.store, { starting: false })
    }
  }

  /** getUserMedia with the remembered devices, then the real device list. */
  private async acquire(): Promise<void> {
    const { prefs } = this.s
    await this.local.start({
      ...(prefs.mic ? { audioDeviceId: prefs.mic } : {}),
      ...(prefs.camera ? { videoDeviceId: prefs.camera } : {}),
    })
    await this.settleDevices()
  }

  /**
   * Open the camera and microphone for the green room: no transport, nothing
   * published, nothing on the wire. Everything that works in the call — mute,
   * camera off, device switching, the level meter — works here on the same
   * tracks, because it is the same controller. Idempotent.
   */
  async preview(): Promise<void> {
    if (this.local.state.get().stream || this.s.previewing || this.s.starting) return
    patch(this.store, { previewing: true })
    try {
      await this.acquire()
    } finally {
      patch(this.store, { previewing: false })
    }
  }

  /**
   * Whether we may send our microphone at all. A big meeting's attendee may
   * not until a host lets them speak (the server and the media server hold
   * the same line). Not allowed: muted, unpublished and closed — the mic
   * light goes out. Allowed again, in the call: opened and published,
   * muted, one tap from speaking.
   */
  async setMicAllowed(allowed: boolean): Promise<void> {
    if (allowed === this.micAllowed) return
    this.micAllowed = allowed
    if (!allowed) {
      this.setMuted(true)
      this.published.mic?.stop()
      this.published.mic = undefined
      this.local.stopAudio()
      return
    }
    // Before the call, the green room and start() open it as usual.
    if (!this.s.started) return
    this.micOpening = (async () => {
      const track = await this.local.switchAudio(this.s.prefs.mic ?? null)
      if (!track || !this.micAllowed) return
      this.setMuted(true)
      const transport = this.transportStore.get()
      if (transport && !this.published.mic) {
        this.published.mic = await transport.publish(track, { kind: 'microphone' })
      }
      // Publishing switches a track on (the SFU's client does): muted means
      // muted, so it is said again after. Without this the mic sent sound
      // while every screen, its owner's included, showed it muted.
      this.setMuted(this.s.micMuted)
    })()
    await this.micOpening
  }

  /**
   * Whether we may send our camera at all, the same way. Not allowed, it is
   * turned off and cannot be turned on; allowed again, it stays off until
   * its owner turns it on.
   */
  async setCameraAllowed(allowed: boolean): Promise<void> {
    this.cameraAllowed = allowed
    if (!allowed && this.local.state.get().videoTrack) await this.setCameraEnabled(false)
  }

  /** Resolves once a microphone being opened again (setMicAllowed) is ready to unmute. */
  whenMicReady(): Promise<void> {
    return this.micOpening
  }

  /** Mute keeps the track alive — silence is cheap and resume is instant. */
  setMuted(muted: boolean): void {
    this.local.setAudioEnabled(!muted)
    patch(this.store, { micMuted: muted })
  }

  /**
   * Camera off STOPS the track so the hardware LED goes out, and replaces the
   * sender's track with null. replaceTrack does not renegotiate, so toggling
   * costs nothing on the wire.
   *
   * Resolves true once the hardware is in the requested state. Turning on can
   * fail — device unplugged, taken by another app — and the caller must not
   * announce a camera that never opened.
   */
  async setCameraEnabled(enabled: boolean): Promise<boolean> {
    if (!enabled) {
      this.local.stopVideo()
      await this.published.camera?.replace(null)
      return true
    }

    if (!this.cameraAllowed) return false
    const track = await this.local.resumeVideo()
    if (!track) return false

    const transport = this.transportStore.get()
    if (this.published.camera) await this.published.camera.replace(track)
    else if (transport) {
      this.published.camera = await transport.publish(track, {
        kind: 'camera',
        contentHint: 'motion',
      })
    }
    return true
  }

  /**
   * Subscribed quality per peer: the higher of two asks. The stage's, from
   * each tile's size on this screen; and, while a recording runs, the
   * recorder's, from each tile's size in the recording's frame — a face
   * that is a thumbnail here may be half the recording, and the recording
   * is the output, not the tile. A no-op in mesh: there is nobody to ask
   * for a smaller layer. In the SFU it is the single biggest lever on CPU
   * and bandwidth.
   */
  setTileQualities(wanted: Record<PeerId, Quality>): void {
    this.stageWants = wanted
    this.applyWantedQualities()
  }

  /** What a running recording needs; null when none runs. */
  setRecorderQualities(wanted: Record<PeerId, Quality> | null): void {
    this.recorderWants = wanted
    this.applyWantedQualities()
  }

  /**
   * Save data, or not: cameras stop arriving (the stage shows each person's
   * picture instead), sound and shared screens carry on. For this page only: the next call
   * starts with cameras, and the stage says so while it is on.
   */
  setDataSaver(on: boolean): void {
    patch(this.store, { dataSaver: on })
    this.applyWantedQualities()
  }

  /**
   * Out of sight for a few seconds — another tab, another app, a locked
   * phone — nobody's camera is worth receiving: it stops until the page is
   * back, and comes back at once. Sound never stops.
   */
  private watchVisibility(): void {
    if (typeof document === 'undefined') return
    let timer: ReturnType<typeof setTimeout> | null = null
    const follow = (): void => {
      if (timer) clearTimeout(timer)
      timer = null
      if (document.visibilityState === 'visible') {
        if (this.s.backgrounded) {
          patch(this.store, { backgrounded: false })
          this.applyWantedQualities()
        }
        return
      }
      timer = setTimeout(() => {
        patch(this.store, { backgrounded: true })
        this.applyWantedQualities()
      }, BACKGROUND_AFTER_MS)
    }
    document.addEventListener('visibilitychange', follow)
    // A page opened out of sight (a background tab) never changes: start there.
    follow()
  }

  private applyWantedQualities(): void {
    const mesh = this.transportStore.get()
    if (!mesh) return
    const peers = new Set([
      ...Object.keys(this.stageWants),
      ...Object.keys(this.recorderWants ?? {}),
    ])
    // Saving data, or out of sight: the screen asks for nothing. A running
    // recording still gets what its frame needs — it is the output.
    const stageOff = this.s.dataSaver || this.s.backgrounded
    for (const peerId of peers) {
      const quality = higherQuality(
        stageOff ? 'off' : (this.stageWants[peerId] ?? 'off'),
        this.recorderWants?.[peerId] ?? 'off',
      )
      const trackId = this.s.remotes[peerId]?.stream.getVideoTracks()[0]?.id
      if (trackId) mesh.setSubscribedQuality(peerId, trackId, quality)
    }
  }

  // ---------------------------------------------------------------- sharing

  /**
   * Present. The browser's picker, then the tracks onto the media plane —
   * a transceiver of their own, so the camera is never touched — then the
   * room is told. Resolves false when the person closed the picker, when
   * the room refused (the room setting, say), or when capture failed; the
   * last two say why in `warning`.
   */
  async startShare(): Promise<boolean> {
    const mesh = this.transportStore.get()
    if (!mesh || this.s.share || this.s.shareBusy) return false
    patch(this.store, { shareBusy: true })
    try {
      const captured = await captureScreen()
      if (!captured) return false
      const hint = defaultHint({ surface: captured.surface, hasAudio: captured.audio !== null })
      const video = await mesh.publish(captured.video, { kind: 'screen', contentHint: hint })
      const audio = captured.audio
        ? await mesh.publish(captured.audio, { kind: 'screen-audio' })
        : null
      const share: LocalShare = {
        stream: captured.stream,
        video,
        audio,
        surface: captured.surface,
        hint,
        trackId: captured.video.id,
      }
      patch(this.store, { share })
      // The browser's own "Stop sharing" bar ends the track without asking
      // us. The app must follow, everywhere, or the room keeps a presenter
      // who is presenting nothing.
      captured.video.addEventListener(
        'ended',
        () => {
          if (this.s.share === share) void this.stopShare('browser')
        },
        { once: true },
      )
      const told = await this.room.startScreenShare({
        trackId: share.trackId,
        surface: share.surface,
        hasAudio: audio !== null,
      })
      if (!told.ok) {
        await this.stopShare('refused')
        return false
      }
      return true
    } catch (err) {
      const message =
        err instanceof ScreenCaptureError
          ? captureFailureMessage(err.failure)
          : `Could not start presenting: ${err instanceof Error ? err.message : String(err)}`
      if (message) patch(this.store, { warning: message })
      return false
    } finally {
      patch(this.store, { shareBusy: false })
    }
  }

  /**
   * Stop presenting. Everything local ends at once — the capture, the
   * senders — whatever the reason; the room is told only when the end is
   * ours (a person, or the browser's bar). Replaced or stopped by a host,
   * the room already knows; leaving, the seat goes with us.
   */
  async stopShare(reason: ShareEnd = 'user'): Promise<void> {
    const share = this.s.share
    if (!share) return
    patch(this.store, { share: null, screenProfile: null, screenSentTo: {} })
    share.video.stop()
    share.audio?.stop()
    for (const track of share.stream.getTracks()) track.stop()
    if (reason === 'user' || reason === 'browser') await this.room.stopScreenShare()
  }

  /** Meet's "optimise for video": smooth motion over sharp text, or back. */
  setShareHint(hint: ContentHint): void {
    const share = this.s.share
    if (!share) return
    share.video.setContentHint(hint)
    patch(this.store, { share: { ...share, hint } })
  }

  /**
   * After a rejoin the server may hold no presenter (a restart) or the
   * wrong one; if we are still capturing, say so again. The media plane
   * republishes existing tracks to every peer on its own.
   */
  async reannounceShare(): Promise<void> {
    const share = this.s.share
    const { room, me } = this.room.state.get()
    if (!share || room.screenShare?.peerId === me) return
    const told = await this.room.startScreenShare({
      trackId: share.trackId,
      surface: share.surface,
      hasAudio: share.audio !== null,
    })
    if (!told.ok) await this.stopShare('refused')
  }

  /**
   * The room's slot is the truth about who presents. If it leaves us while
   * we are still capturing, someone took over or a host stopped us: end the
   * capture and say which.
   */
  private followPresenterSlot(): void {
    let previous: { holder: PeerId | null; me: PeerId | null } | null = null
    this.room.state.subscribe(({ room, me }) => {
      const holder = room.screenShare?.peerId ?? null
      const before = previous
      previous = { holder, me }
      if (!before || (before.holder === holder && before.me === me)) return
      const share = this.s.share
      if (!share || !me || holder === me) return
      if (holder) {
        const name = room.participants[holder]?.displayName ?? 'Someone else'
        void this.stopShare('replaced')
        patch(this.store, {
          warning: `${name} is presenting now, so your presentation has stopped.`,
        })
      } else if (before.holder === me) {
        void this.stopShare('host')
        patch(this.store, { warning: 'A host stopped your presentation.' })
      }
    })
  }

  // ---------------------------------------------------------------- quality

  /**
   * One line about the share toward one peer, for the diagnostics: what
   * size is going out, at what rate, in which codec, how long each frame
   * takes to encode, what is holding the encoder back and how far the
   * adapter has stepped down, and how the packets travel. Rates are the
   * change since the previous poll.
   */
  private describeScreenSend(s: PeerStats, prev: PeerStats | null): string {
    const parts: string[] = []
    const fps = s.screenSentFramesPerSecond ? `@${Math.round(s.screenSentFramesPerSecond)}` : ''
    parts.push(`${s.screenSentWidth}×${s.screenSentHeight}${fps}`)
    if (prev && s.screenBytesSent !== null && prev.screenBytesSent !== null) {
      const bytes = s.screenBytesSent - prev.screenBytesSent
      if (bytes >= 0) parts.push(`${Math.round((bytes * 8) / QUALITY_POLL_MS)} kbps`)
      // The SFU's reports carry no encode time; the mesh's do.
      if (s.screenEncodeTimeSeconds !== null && prev.screenEncodeTimeSeconds !== null) {
        const frames = (s.screenFramesEncoded ?? 0) - (prev.screenFramesEncoded ?? 0)
        const seconds = s.screenEncodeTimeSeconds - prev.screenEncodeTimeSeconds
        if (frames > 0 && seconds >= 0) {
          parts.push(`${Math.round((seconds / frames) * 1000)} ms/frame`)
        }
      }
    }
    if (s.screenCodec) parts.push(s.screenCodec.replace(/^video\//i, ''))
    if (s.screenScale && s.screenScale !== 1) parts.push(`↓${s.screenScale}×`)
    if (s.screenLimitation !== 'none') parts.push(`limited by ${s.screenLimitation}`)
    parts.push(s.path)
    return parts.join(' · ')
  }

  /**
   * Rate every link on the change since the last sample, with hysteresis
   * per link, and report our own quality as the best of them. The reasoning
   * for "best" lives with ownQuality(); the thresholds with rateLink().
   */
  private async pollQuality(): Promise<void> {
    const mesh = this.transportStore.get()
    if (!mesh) return
    let stats: TransportStats
    try {
      stats = await mesh.getStats()
    } catch {
      return // a connection closing mid-poll; the next tick will know
    }

    // The previous poll's samples, before they are replaced below: the share's
    // rate and encode time are differences between two polls.
    const previous = new Map(this.lastSamples)

    const linkQuality: Record<PeerId, ConnectionQuality> = {}
    const linkNumbers: Record<PeerId, LinkNumbers> = {}
    for (const sample of Object.values(stats.perPeer)) {
      let tracker = this.linkTrackers.get(sample.peerId)
      if (!tracker) {
        // The SFU's one link stands for the whole call and is rated on a
        // cellular phone's jitter as much as anything: ask to see a
        // worsening twice, and a recovery three times, before saying so.
        tracker =
          mesh.name === 'sfu' ? new QualityTracker('good', 3, 2) : new QualityTracker('good')
        this.linkTrackers.set(sample.peerId, tracker)
      }
      const w = windowOf(this.lastSamples.get(sample.peerId) ?? null, sample)
      const total = w.packetsReceived + w.packetsLost
      linkNumbers[sample.peerId] = {
        rttMs: w.roundTripTimeMs,
        jitterMs: w.jitterMs,
        lossPct: total > 0 ? Math.round((w.packetsLost / total) * 1000) / 10 : 0,
        freezes: w.freezes,
      }
      const rating = rateLink(w)
      this.lastSamples.set(sample.peerId, sample)
      linkQuality[sample.peerId] = tracker.update(rating)
    }
    for (const peerId of this.linkTrackers.keys()) {
      if (!(peerId in linkQuality)) {
        this.linkTrackers.delete(peerId)
        this.lastSamples.delete(peerId)
      }
    }

    const sentTo: Record<PeerId, string> = {}
    const receivedFrom: Record<PeerId, string> = {}
    const screenSentTo: Record<PeerId, string> = {}
    const linkPath: Record<PeerId, string> = {}
    for (const s of Object.values(stats.perPeer)) {
      linkPath[s.peerId] = s.path
      if (s.frameWidth && s.frameHeight) {
        receivedFrom[s.peerId] =
          `${s.frameWidth}×${s.frameHeight}${s.receivedCodec ? ` ${s.receivedCodec}` : ''}`
      }
      if (s.sentWidth && s.sentHeight) {
        const fps = s.sentFramesPerSecond ? `@${Math.round(s.sentFramesPerSecond)}` : ''
        sentTo[s.peerId] = `${s.sentWidth}×${s.sentHeight}${fps}`
      }
      if (s.screenSentWidth && s.screenSentHeight) {
        screenSentTo[s.peerId] = this.describeScreenSend(s, previous.get(s.peerId) ?? null)
      }
    }

    // Our own encoder's verdict caps the result: a CPU limit is ours, and a
    // bandwidth limit on every path is our uplink.
    let own = ownQuality(Object.values(linkQuality))
    if (own && stats.qualityLimitationReason !== 'none' && QUALITY_RANK[own] > QUALITY_RANK.good) {
      own = 'good'
    }
    patch(this.store, {
      linkNumbers,
      linkQuality,
      sendProfile: stats.sendProfile,
      screenProfile: stats.screenProfile,
      sentTo,
      receivedFrom,
      sendLayers: stats.sendLayers ?? '',
      screenSentTo,
      linkPath,
      quality: own,
    })
  }

  // ---------------------------------------------------------------- devices

  private remember(kind: keyof DevicePrefs, deviceId: string | null): void {
    patch(this.store, { prefs: { ...this.s.prefs, [kind]: deviceId } })
    saveDevicePref(this.localStorage, kind, deviceId)
  }

  /**
   * Runs once after the first successful start, when labels and ids are
   * finally complete. A remembered choice that is no longer plugged in is
   * forgotten rather than retried on every join. The speaker is resolved the
   * same way the mic is: last choice, else the OS's call device, else default.
   */
  private async settleDevices(): Promise<void> {
    await this.devices.refresh()
    const { mics, cameras, speakers } = this.devices.state.get()
    const { prefs } = this.s
    if (prefs.mic && !mics.some((d) => d.deviceId === prefs.mic)) this.remember('mic', null)
    if (prefs.camera && !cameras.some((d) => d.deviceId === prefs.camera)) {
      this.remember('camera', null)
    }
    if (this.devices.supportsSpeakerSelection) {
      patch(this.store, { speakerId: preferredDevice(speakers, this.s.prefs.speaker) ?? '' })
    }
    this.unfollowDevices ??= this.devices.follow(() => this.onDevicesChanged())
  }

  /**
   * Plug or unplug. Input loss is handled by the track's own `ended` event
   * (see LocalMedia.watchLoss), which is more reliable than diffing lists.
   * The speaker has no track, so it is the one thing checked against the list.
   */
  private onDevicesChanged(): void {
    const { speakerId, prefs } = this.s
    if (!speakerId) return
    const { speakers } = this.devices.state.get()
    if (speakers.some((d) => d.deviceId === speakerId)) return
    const next = preferredDevice(speakers, prefs.speaker) ?? ''
    patch(this.store, {
      speakerId: next,
      warning: `Your speaker was disconnected. Audio now plays through ${
        next ? labelOf(speakers, next) : 'the system default'
      }.`,
    })
  }

  /**
   * Switch microphones mid-call: new getUserMedia, replaceTrack on the sender
   * — no renegotiation — then the old track is stopped. Peers notice nothing
   * but the voice changing.
   */
  async selectMicrophone(deviceId: string | null): Promise<boolean> {
    // Not ours to send: the choice is kept for when it is.
    if (!this.micAllowed && this.s.started) {
      this.remember('mic', deviceId)
      return true
    }
    patch(this.store, { switching: true })
    try {
      const track = await this.local.switchAudio(deviceId)
      if (!track) return false
      this.remember('mic', deviceId)
      const transport = this.transportStore.get()
      if (this.published.mic) await this.published.mic.replace(track)
      else if (transport) {
        this.published.mic = await transport.publish(track, { kind: 'microphone' })
        // A fresh publication switches the track on: keep a muted mic muted.
        this.setMuted(this.s.micMuted)
      }
      return true
    } finally {
      patch(this.store, { switching: false })
    }
  }

  /** Same for the camera. With the camera off, the choice waits for the next resume. */
  async selectCamera(deviceId: string | null): Promise<boolean> {
    patch(this.store, { switching: true })
    try {
      if (!(await this.local.switchVideo(deviceId))) return false
      this.remember('camera', deviceId)
      const track = this.local.state.get().videoTrack
      if (!track) return true
      const transport = this.transportStore.get()
      if (this.published.camera) await this.published.camera.replace(track)
      else if (transport) {
        this.published.camera = await transport.publish(track, {
          kind: 'camera',
          contentHint: 'motion',
        })
      }
      return true
    } finally {
      patch(this.store, { switching: false })
    }
  }

  /** Output belongs to the audio elements, not to any track; they follow `speakerId`. */
  selectSpeaker(deviceId: string): void {
    patch(this.store, { speakerId: deviceId })
    this.remember('speaker', deviceId || null)
  }

  /** setSinkId rejected: the device went away between listing and use, or the browser refused it. */
  speakerFailed(deviceId: string): void {
    if (this.s.speakerId !== deviceId) return
    patch(this.store, {
      speakerId: '',
      warning: 'That speaker could not be used. Audio is playing through the system default.',
    })
    this.remember('speaker', null)
  }

  /**
   * The device behind a live track went away — unplugged, or taken by the OS.
   * The call must not drop with it: fall back to whatever the platform now
   * considers the right device, and say so. If no camera is left, the camera
   * is turned off for everyone rather than left "on" with nothing behind it.
   */
  private async handleLost(kind: 'audio' | 'video'): Promise<void> {
    if (kind === 'audio') {
      this.remember('mic', null)
      const ok = await this.selectMicrophone(null)
      patch(this.store, {
        warning: ok
          ? `Your microphone was disconnected. Now using ${labelOf(this.devices.state.get().mics, this.activeMicId.get())}.`
          : 'Your microphone was disconnected, and no other microphone could be opened.',
      })
      return
    }

    this.remember('camera', null)
    if (await this.setCameraEnabled(true)) {
      patch(this.store, {
        warning: `Your camera was disconnected. Now using ${labelOf(this.devices.state.get().cameras, this.activeCameraId.get())}.`,
      })
      return
    }
    await this.published.camera?.replace(null)
    await this.room.setCamera(false)
    patch(this.store, { warning: 'Your camera was disconnected.' })
  }

  stop(): void {
    void this.stopShare('leave')
    if (this.qualityTimer) {
      clearInterval(this.qualityTimer)
      this.qualityTimer = null
    }
    this.linkTrackers.clear()
    this.lastSamples.clear()
    this.analysisTrack.get()?.stop()
    this.analysisTrack.set(null)
    this.unfollowDevices?.()
    this.unfollowDevices = null
    this.transportStore.get()?.close()
    this.transportStore.set(null)
    this.local.stop()
    this.published = {}
    patch(this.store, {
      transportName: null,
      screens: {},
      quality: null,
      linkQuality: {},
      linkNumbers: {},
      sendProfile: null,
      sentTo: {},
      receivedFrom: {},
      sendLayers: '',
      linkPath: {},
      micMuted: false,
      warning: null,
      startError: null,
      audioBlocked: false,
      started: false,
      switching: false,
      remotes: {},
      peerStates: {},
    })
  }
}
